import { api } from "@/lib/api/client";
import type {
  BanAppeal,
  GlobalBan,
  GlobalBanProposal,
  IncidentCategory,
  ModerationAction,
  ModerationActionResponse,
  ModerationDetailResponse,
  ModerationFilters,
  ModerationListResponse,
  ProposalStatus,
  SecurityIncident,
} from "./types";

function build(params: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === null || v === false) continue;
    out[k] = v === true ? "1" : String(v);
  }
  return out;
}

// ── Event Moderation ─────────────────────────────────────────────────
export const moderationApi = {
  list: async (filters: ModerationFilters): Promise<ModerationListResponse> => {
    const { data } = await api.get<ModerationListResponse>(
      "/admin/events-moderation",
      { params: build({ ...filters }) },
    );
    return data;
  },
  detail: async (id: number): Promise<ModerationDetailResponse> => {
    const { data } = await api.get<ModerationDetailResponse>(
      `/admin/events-moderation/${id}`,
    );
    return data;
  },
  apply: async (
    id: number,
    action: ModerationAction,
    reason: string,
  ): Promise<ModerationActionResponse> => {
    const { data } = await api.post<ModerationActionResponse>(
      `/admin/events-moderation/${id}/${action}`,
      { reason },
    );
    return data;
  },
};

// ── Security Incidents (read-only) ───────────────────────────────────
export const incidentsApi = {
  list: async (params: { limit?: number; offset?: number }): Promise<SecurityIncident[]> => {
    const { data } = await api.get<SecurityIncident[]>(
      "/api/admin/security/incidents",
      { params: build({ ...params }) },
    );
    return data;
  },
};

// ── Global Ban Proposals ─────────────────────────────────────────────
export const proposalsApi = {
  list: async (params: { status?: ProposalStatus }): Promise<GlobalBanProposal[]> => {
    const { data } = await api.get<GlobalBanProposal[]>(
      "/api/admin/security/proposals",
      { params: build({ ...params }) },
    );
    return data;
  },
  detail: async (id: number): Promise<GlobalBanProposal> => {
    const { data } = await api.get<GlobalBanProposal>(
      `/api/admin/security/proposals/${id}`,
    );
    return data;
  },
  approve: async (
    id: number,
    body: {
      decisionReason: string;
      expiresAt: string | null;
      appealable: boolean;
      publicReason?: string;
      category?: IncidentCategory;
    },
  ): Promise<GlobalBan> => {
    const { data } = await api.put<GlobalBan>(
      `/api/admin/security/proposals/${id}/approve`,
      body,
    );
    return data;
  },
  reject: async (id: number, decisionReason: string): Promise<GlobalBanProposal> => {
    const { data } = await api.put<GlobalBanProposal>(
      `/api/admin/security/proposals/${id}/reject`,
      { decisionReason },
    );
    return data;
  },
  requestMoreInfo: async (id: number, requestedInfo: string): Promise<GlobalBanProposal> => {
    const { data } = await api.put<GlobalBanProposal>(
      `/api/admin/security/proposals/${id}/request-info`,
      { requestedInfo },
    );
    return data;
  },
};

// ── Global Bans (active) ─────────────────────────────────────────────
export const globalBansApi = {
  list: async (): Promise<GlobalBan[]> => {
    const { data } = await api.get<GlobalBan[]>("/api/admin/security/global-bans");
    return data;
  },
  revoke: async (id: number, reason: string): Promise<GlobalBan> => {
    const { data } = await api.put<GlobalBan>(
      `/api/admin/security/global-bans/${id}/revoke`,
      { reason },
    );
    return data;
  },
};

// ── Appeals ──────────────────────────────────────────────────────────
export const appealsApi = {
  list: async (): Promise<BanAppeal[]> => {
    const { data } = await api.get<BanAppeal[]>("/api/admin/security/appeals");
    return data;
  },
  resolve: async (
    id: number,
    approve: boolean,
    decisionReason: string,
  ): Promise<BanAppeal> => {
    const { data } = await api.put<BanAppeal>(
      `/api/admin/security/appeals/${id}/resolve`,
      { approve, decisionReason },
    );
    return data;
  },
};
